import React, { Fragment, useState, useRef } from 'react';
import { Link } from 'gatsby';
import { StyleSheet, css } from 'aphrodite';
import { FluidObject } from 'gatsby-image';
import slugify from 'slugify';
import BackgroundImage from 'gatsby-background-image';
import { isMobile } from 'react-device-detect';

import TippyTooltip from 'components/TippyTooltip/TippyTooltip';
import { white } from 'shared/colors';
import { BREAKPOINTS } from 'shared/constants';

interface Props {
  id: string;
  imageAltText: string;
  productVariants: {
    id: string;
    name: string;
    potency: 'Light' | 'Medium' | 'Heavy';
    potencyValue: string;
    price: string;
  }[];
  fluid: FluidObject;
  slug: string;
  productName: string;
  index: number;
  isSingle: boolean;
  packagingName?: string;
  isPackagingChoices: boolean;
  threedmodel?: string;
  useThreedModelAsThumb?: boolean;
  useVideoOnHoverAsThumbnail?: boolean;
  backgroundVideo?: {
    file: {
      url: string;
    };
  };
}

const ProductCard = ({
  id,
  imageAltText,
  productVariants,
  fluid,
  slug,
  productName,
  index,
  isSingle,
  packagingName,
  isPackagingChoices,
  threedmodel,
  useThreedModelAsThumb,
  useVideoOnHoverAsThumbnail,
  backgroundVideo,
}: Props) => {
  const [isOver, setIsOver] = useState(false);
  const vidRef = useRef<HTMLVideoElement | null>(null);

  const isLeft = index === 0;
  const isRight = index === 2;

  const handleMouseOver = () => {
    if (isMobile) return;
    setIsOver(true);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    setIsOver(false);
  };

  return (
    <Fragment key={id}>
      {productVariants.map(({ id, name, potency, potencyValue, price }) => {
        const strippedProductName = productName
          ? productName.replace(/[^\w\s]/gi, '-')
          : '';

        const strippedPackagingChoiceName =
          packagingName && packagingName.replace(/[^\w\s]/gi, '-');

        const product = slugify(
          strippedProductName ? strippedProductName.toLowerCase() : ''
        );

        let packagingChoiceProduct;
        if (strippedPackagingChoiceName) {
          packagingChoiceProduct = slugify(
            strippedPackagingChoiceName.toLowerCase()
          );
        }

        const productPotency = potency.toLowerCase();
        const shopPath = `/products/${slug}/${
          !isPackagingChoices ? product : packagingChoiceProduct
        }/${productPotency}`;

        const Content = () => (
          <>
            <p className={css(styles.productName)}>{name}</p>
            <TippyTooltip>
              <p className={css(styles.productText, styles.productPotency)}>
                {potency}
              </p>
            </TippyTooltip>
            <p className={css(styles.productText, styles.productPrice)}>
              {price}
            </p>
          </>
        );

        return (
          <Link
            key={id}
            to={shopPath}
            className={css(
              styles.product,
              isLeft && styles.isLeft,
              isSingle && styles.isLeftSingle,
              isRight && styles.isRight
            )}
            onMouseEnter={handleMouseOver}
            onMouseLeave={handleMouseLeave}
          >
            {useThreedModelAsThumb && threedmodel ? (
              <div className={css(styles.productImage)}>
                <Content />
                <iframe
                  className={css(styles.threedmodel)}
                  src={threedmodel}
                  frameBorder="0"
                />
              </div>
            ) : useVideoOnHoverAsThumbnail && backgroundVideo ? (
              <div>
                <BackgroundImage
                  fluid={fluid}
                  className={css(styles.productImage)}
                  alt={imageAltText}
                >
                  <>
                    {!isMobile && (
                      <video
                        autoPlay
                        playsInline
                        muted
                        loop
                        className={css([
                          styles.video,
                          !isOver && styles.videoHide,
                        ])}
                        ref={vidRef}
                        aria-hidden
                      >
                        <source src={backgroundVideo.file.url} />
                        Sorry, your browser doesn't support embedded video
                      </video>
                    )}
                    <Content />
                  </>
                </BackgroundImage>
              </div>
            ) : (
              <BackgroundImage
                fluid={fluid}
                className={css(styles.productImage)}
                alt={imageAltText}
              >
                <Content />
              </BackgroundImage>
            )}
          </Link>
        );
      })}
    </Fragment>
  );
};

export default ProductCard;

const fadeinKeyframes = {
  '0%': {
    display: 'none',
  },
  '100%': {
    display: 'block',
  },
};

const styles = StyleSheet.create({
  product: {
    width: '100%',
    color: white,
    textDecoration: 'none',
    height: 'calc(50vw / 8 * 5)',
    borderBottom: `1px solid ${white}`,
    fontFamily: 'Titling Gothic FB Narrow Standard',
    textTransform: 'uppercase',
    flex: '0 50%',
    ':focus': {
      outline: `4px dotted ${white}`,
    },
    [BREAKPOINTS.TABLET]: {
      height: 'calc(100vw / 8 * 5)',
    },
    [BREAKPOINTS.MOBILE]: {},
  },
  isLeft: {
    borderRight: `1px solid ${white}`,
    [BREAKPOINTS.TABLET]: {
      borderRight: 'none',
      width: '100%',
    },
  },
  isLeftSingle: {
    borderRight: `1px solid ${white}`,
    [BREAKPOINTS.TABLET]: {
      borderRight: 'none',
      width: '100%',
    },
  },
  isRight: {
    borderLeft: `1px solid ${white}`,
    [BREAKPOINTS.TABLET]: {
      borderLeft: 'none',
      width: '100%',
    },
  },
  productImage: {
    height: 'calc(49.8vw / 8 * 5)',
    width: '100%',
    objectFit: 'cover',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    paddingBottom: '20px',
    paddingLeft: '20px',
    position: 'relative',
    [BREAKPOINTS.TABLET]: {
      width: '100%',
      height: 'calc(99.8vw / 8 * 5)',
    },
    [BREAKPOINTS.MOBILE]: {},
  },
  productName: {
    fontFamily: 'Titling Gothic FB Normal Regular',
    fontWeight: 'bold',
    fontSize: '11px',
    lineHeight: 1.29,
    zIndex: 10,
    [BREAKPOINTS.TABLET]: {},
    [BREAKPOINTS.MOBILE]: {},
  },
  productText: {
    fontFamily: 'Titling Gothic FB Normal Regular',
    fontSize: '11px',
    lineHeight: 1.72,
    zIndex: 10,
    [BREAKPOINTS.TABLET]: {},
    [BREAKPOINTS.MOBILE]: {},
  },
  productPotency: {
    [BREAKPOINTS.TABLET]: {},
  },
  potencyValue: {
    textDecoration: 'underline',
  },
  productPrice: {
    fontFamily: 'Titling Gothic FB Normal Regular',
    fontSize: '11px',
    lineHeight: 1.5,
    marginTop: '1vw',
  },
  threedmodel: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    background: 'none',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,

    [BREAKPOINTS.TABLET]: {
      display: 'none',
    },
  },
  video: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    aspectRatio: '16 / 9',
    objectFit: 'cover',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    transition: 'all 0.3s ease-in-out',
    animationName: [fadeinKeyframes],
    animationDuration: '3s',
    [BREAKPOINTS.TABLET]: {
      display: 'none',
    },
  },
  videoHide: {
    opacity: 0,
    transition: 'all 0.3s ease-in-out',
  },

  KeyframeEffect: {},
});
